import PropTypes from 'prop-types';

// material-ui
import { Avatar, Box, Button, ButtonBase, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project imports
import LogoSection from '../LogoSection';
import ProfileSection from './ProfileSection';

// assets
import { ArrowBack } from '@mui/icons-material';
import { IconMenu2 } from '@tabler/icons-react';
import { useAuth } from 'context/AuthContext';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardSwitcher from './Switcher';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

const Header = ({ handleLeftDrawerToggle }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin } = useAuth();
    const [dashboard, setDashboard] = useState('Main');
    const firstPart = location.pathname.split('/')[1];

    useEffect(() => {
        if (
            firstPart === 'civils' ||
            firstPart === 'accounting' ||
            firstPart === 'procurement' ||
            firstPart === 'qs' ||
            firstPart === 'architecture'
        ) {
            setDashboard(firstPart.charAt(0).toUpperCase() + firstPart.slice(1));
        } else {
            setDashboard('Main');
        }
    }, [firstPart]);

    const isMainDashboard = dashboard === 'Main';
    const dashboardTitle =
        (dashboard === 'Main' && 'Main Dashboard') || (dashboard === 'Qs' && 'Quantity Surveyors Dashboard') || `${dashboard} Dashboard`;

    return (
        <>
            {/* logo & toggler button */}
            <Box
                sx={{
                    width: 228,
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    },
                    alignItems: 'center'
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>
                <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.secondary.light,
                            color: theme.palette.secondary.dark,
                            '&:hover': {
                                background: theme.palette.secondary.dark,
                                color: theme.palette.secondary.light
                            }
                        }}
                        onClick={handleLeftDrawerToggle}
                        color="inherit"
                    >
                        <IconMenu2 stroke={1.5} size="1.3rem" />
                    </Avatar>
                </ButtonBase>
            </Box>

            {/* header search */}
            <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1 }}>
                {isAdmin && !isMainDashboard && (
                    <Button
                        size="small"
                        onClick={() => navigate('/dashboard/default')}
                        sx={{
                            ml: 2,
                            borderRadius: '50px',
                            px: 2,
                            color: '#1976d2',
                            backgroundColor: '#fff',

                            '& .animated-text': {
                                display: 'inline-block',
                                animation: 'textPulse 3s ease-in-out infinite'
                            },

                            '@keyframes textPulse': {
                                '0%': {
                                    transform: 'scale(1)',
                                    opacity: 1
                                },
                                '50%': {
                                    transform: 'scale(1.1)',
                                    opacity: 0.7
                                },
                                '100%': {
                                    transform: 'scale(1)',
                                    opacity: 1
                                }
                            },

                            '&:hover': {
                                color: '#fff',
                                backgroundColor: '#1976d2'
                            }
                        }}
                    >
                        <span className="animated-text">Main Dashboard</span>
                    </Button>
                )}
                <Typography
                    sx={{
                        fontSize: { md: '2.125rem', sm: '2rem', xs: '1.5rem' },
                        fontWeight: 500,
                        mr: 1,
                        mt: 1.75,
                        mb: 0.75,
                        ml: 3,
                        fontStyle: 'italic',
                        color: theme.palette.secondary.dark,
                        display: { xs: 'none', sm: 'none', md: 'block' }
                    }}
                >
                    {dashboardTitle}
                </Typography>
                {isAdmin && isMainDashboard && <DashboardSwitcher current={dashboard} onChange={(dash) => setDashboard(dash)} />}
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            {/* notification & profile */}
            <ProfileSection />
            {/* <NotificationSection /> */}
            {/* <ProfileSection /> */}
        </>
    );
};

Header.propTypes = {
    handleLeftDrawerToggle: PropTypes.func
};

export default Header;
